/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";

import { Box, Pagination, Grid, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import moment from "moment";

import { getJobs } from "../../../redux/jobs/actions";
import JobCard from "../../../components/shared/JobCard";
import { JobCategories, Roles } from "../../../utilities/constants";
import Table from "../../../components/shared/Table";
import { capitalizeFirstLetter } from "../../../utilities";
import Link from "../../../components/shared/Link";
import { getCurrentUserSelector } from "../../../redux/users/selector";
import Filter from "../../../components/shared/Filter";
import { filterFormFields, filterFormFields2, filterFormSchema } from "./components/filterFormFields";

const columns = (user) => [
  {
    id: "id",
    label: "ID",
    props: {
      align: "center",
    },
  },
  {
    id: "title",
    label: "Title",
    value: (row) => (
      <Typography
        component={RouterLink}
        to={`/jobs/${row.id}`}
        sx={{
          textDecoration: "none",
          "&:hover": {
            color: "#C02026",
          },
        }}
        onClick={() => {
          Cookies.remove("isFromDashboard");
        }}>
        {row?.title}
      </Typography>
    ),
    props: {
      align: "center",
    },
  },
  {
    id: "status",
    label: "Status",
    value: (row) => row.status,
    props: {
      align: "center",
    },
  },
  {
    id: "address",
    label: "Address",
    value: (row) => (
      <span>
        {row?.address &&
          row?.address?.country &&
          row?.address?.city &&
          row?.address?.zipCode &&
          row?.address?.address &&
          `${row?.address?.country}, 
                                ${row?.address?.city} ${row?.address?.zipCode}, 
                                ${row?.address?.address}`}
      </span>
    ),
    props: {
      align: "center",
    },
  },
  {
    id: "createdAt",
    label: "Created Date",
    value: (row) => capitalizeFirstLetter(moment(row.createdAt).format("DD/MM/YYYY")),
  },
  {
    id: "company-view",
    label: "Company",
    type: "button",
    value: (row) => <Link to={`/users/${row.companyOwner}`} content={row?.companyName || "Company"} />,
    condition: Roles.ADMIN.value === user.role,
    props: {
      align: "center",
    },
  },
  {
    id: "category",
    label: "Category",
    value: (row) => (row.jobCategories || []).map((i) => JobCategories.getLabel(i)).join(", "),
    props: {
      align: "center",
    },
  },
];

const JobsList = () => {
  const firstJobRef = useRef();
  const dispatch = useDispatch();
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [page, setPage] = useState(0);
  const jobsPerPage = 10;
  const user = useSelector(getCurrentUserSelector);
  const isLoading = useSelector((state) => state.jobs.loading);
  const jobs = useSelector((state) => state.jobs.data);
  const [initialFilterValues, setInitialFilterValues] = useState({});

  useEffect(() => {
    const applyFilterCookie = Cookies.get("applyFilter");

    let parsedCookieFilter = {};

    try {
      parsedCookieFilter = applyFilterCookie ? JSON.parse(applyFilterCookie) : {};
    } catch (e) {
      parsedCookieFilter = {};
    }

    setInitialFilterValues(parsedCookieFilter);

    dispatch(getJobs());
  }, []);

  useEffect(() => {
    if (!jobs || jobs.length === 0) return;
    if (!initialFilterValues || Object.keys(initialFilterValues).length === 0) return;

    let filtered = [...jobs];

    Object.entries(initialFilterValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return;

      switch (key) {
        case "status":
          filtered = filtered.filter((job) => {
            const jobStatus = job.status?.toUpperCase() || "";

            if (value === "ACTIVE") {
              return jobStatus === "ACTIVE";
            }

            if (value === "INACTIVE") {
              return jobStatus !== "ACTIVE";
            }

            return true;
          });

          setInitialFilterValues({});
          break;

        default:
          break;
      }
    });

    setFilteredJobs(filtered);
  }, [jobs]);

  const handlePagination = (pageNumber) => {
    setPage(pageNumber - 1);
    if (firstJobRef?.current) {
      firstJobRef.current.scrollIntoView({ behavior: "smooth", block: "start", top: firstJobRef.current.offsetTop - 300 });
    }
  };

  return (
    !isLoading && (
      <Box sx={{ width: { xs: "100%", md: "70%" }, margin: "auto", mt: "25px" }}>
        {jobs && (
          <Filter
            shouldStartOpen
            items={jobs}
            setFilteredItems={setFilteredJobs}
            filterSteps={user?.role === Roles.ADMIN.value || user?.role === Roles.COMPANY.value ? filterFormFields2 : filterFormFields}
            filterSchema={filterFormSchema}
          />
        )}
        <hr />
        <Box>
          <Box>
            {[Roles.COMPANY.value, Roles.ADMIN.value].includes(user?.role) ? (
              <Table data={filteredJobs || []} columns={columns(user)} isLoading={isLoading} noResultsContent="There are no available jobs." />
            ) : (
              <Box ref={firstJobRef}>
                {filteredJobs &&
                  (filteredJobs.length === 0 ? (
                    <Typography variant="h5" marginTop="10px">
                      No jobs match your criteria. &nbsp; Reset your filter options to see all available jobs.
                    </Typography>
                  ) : (
                    <Box sx={{ marginTop: "20px" }}>
                      <Grid container>
                        {filteredJobs.slice(page * jobsPerPage, page * jobsPerPage + jobsPerPage).map((item) => (
                          <Grid item key={item.title} xs={12} sm={12} md={4} lg={4} sx={{ paddingLeft: "10px" }}>
                            <JobCard jobAd={item} />
                          </Grid>
                        ))}
                      </Grid>
                      <Box>
                        <Pagination
                          onChange={(e, pageNumber) => handlePagination(pageNumber)}
                          count={Math.ceil(filteredJobs.length / jobsPerPage)}
                          variant="outlined"
                          color="primary"
                          sx={{
                            "& .MuiPagination-ul": {
                              justifyContent: "center",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    )
  );
};

export default JobsList;
